import React, { useState, useRef, useEffect } from "react";
import Header from "../components/Header";
import MapComponent from "../components/Map";
import Admin from "../components/Admin";
import Profile from "../components/Profile";
import ClientFleet from "../components/ClientFleet";
import AccountManager from "../components/AccountManager";
import type {
  TCurrentVessel,
  TPort,
  TBunkerPrice,
  TWeather,
  TCrudePrice,
} from "@/types";
import { jwtDecode } from "jwt-decode";
import { useUser } from "../contexts/UserContext";
import "./HomePage.css";

interface DecodedToken {
  "cognito:groups"?: string[];
  email?: string;
  [key: string]: any;
}

const HomePage: React.FC = () => {
  const [showProfile, setShowProfile] = useState(false);

  const [routeCoordinates, setRouteCoordinates] = useState<
    [number, number][][]
  >([]);

  const [routeColors, setRouteColors] = useState<string[]>([]);

  const [bunkerMarkers, setBunkerMarkers] = useState<TPort[][]>([]);

  const [weather, setWeather] = useState<Map<string, TWeather[]>>(new Map());

  const [prices, setPrices] = useState<TBunkerPrice[]>([]);

  const [crudePrice, setCrudePrice] = useState<TCrudePrice | undefined>();

  const [distanceNm, setDistanceNm] = useState<number>(0);

  const [vessels, setVessels] = useState<TCurrentVessel[]>([]);

  const [selectedPortCallDetails, setSelectedPortCallDetails] = useState<{
    vessel: TCurrentVessel | null;
    fromPort: string;
    destinationPort: string;
    eta: string;
  } | null>(null);

  const user = useUser();

  const [userRole, setUserRole] = useState<string>(""); // Default role

  const mapRef = useRef<google.maps.Map | null>(null);

  const idToken = localStorage.getItem("id_token");

  let userEmail = "";

  if (idToken) {
    const decodedToken: DecodedToken = jwtDecode(idToken);

    userEmail = decodedToken.email || localStorage.getItem("user") || "";
  }

  // Track client domain for title
  const [clientDomain, setClientDomain] = useState<string>("");

  useEffect(() => {
    if (userEmail) {
      const params = new URLSearchParams(window.location.search);
      if (params.has("client")) {
        setUserRole("client");
        setClientDomain(params.get("client") || "");
        return;
      }

      switch (user?.role) {
        case "admin":
          setUserRole("admin");
          break;
        case "accountManager":
          setUserRole("accountManager");
          break;
        case "client":
          setUserRole("client");
          break;
        default:
          setUserRole("user");
      }
    }

    document.title = "F&M - Map";
  }, []);

  const handleShowAll = (vessels: TCurrentVessel[]) => {
    setVessels(vessels);
    if (mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      vessels.forEach((vessel) => {
        if (vessel.latitude && vessel.longitude) {
          bounds.extend({ lat: vessel.latitude, lng: vessel.longitude });
        }
      });
      mapRef.current.fitBounds(bounds);
    }
  };

  const handleUpdateVessels = (updatedVessels: TCurrentVessel[]) => {
    setVessels(updatedVessels);
  };

  const handleSelectPortCall = (
    vessel: TCurrentVessel,
    fromPort: string,
    destinationPort: string,
    eta: string
  ) => {
    setSelectedPortCallDetails({ vessel, fromPort, destinationPort, eta });
  };

  return (
    <>
      <main className="homepage-container">
        <Header onProfileClick={() => setShowProfile(true)} sameTabLinks />

        {showProfile && <Profile onClose={() => setShowProfile(false)} />}

        {userRole === "admin" ||
        userRole === "accountManager" ||
        userRole === "client" ? (
          <div className="homepage-content">
            {/* Left Panel: ClientFleet */}
            <div className="left-panel">
              <ClientFleet
                onVesselClick={(lat, lng) =>
                  console.log(`Vessel clicked at ${lat}, ${lng}`)
                }
                onShowAll={handleShowAll}
                onUpdateVessels={handleUpdateVessels}
                setRouteCoordinates={setRouteCoordinates}
                setRouteColors={setRouteColors}
                setBunkerMarkers={setBunkerMarkers}
                setWeather={setWeather}
                setPrices={setPrices}
                setCrudePrice={setCrudePrice}
                distanceNm={distanceNm}
                onSelectPortCall={handleSelectPortCall}
              />
            </div>

            {/* Right Panel: Map */}
            <div className="right-panel">
              <MapComponent
                vessels={vessels}
                routeCoordinates={routeCoordinates}
                routeColors={routeColors}
                bunkerMarkers={bunkerMarkers}
                weather={weather}
                prices={prices}
                crudePrice={crudePrice}
                distanceNm={distanceNm}
                onLoad={(map) => (mapRef.current = map)}
                selectedPortCallDetails={selectedPortCallDetails}
              />
            </div>
          </div>
        ) : (
          "Loading..."
        )}
      </main>
    </>
  );
};

export default HomePage;
